import {
  Component,
  Input,
  ChangeDetectionStrategy,
  Inject,
  PLATFORM_ID,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  OnInit
} from '@angular/core';
import { Deal, Product } from '@vestiaire/api-sdk-angular';
import { isPlatformServer } from '@angular/common';
import { I18nData } from '@app/core/i18n/services';
import { HostnameLocalizationService } from '@app/core/i18n';
import { select, Store } from '@ngrx/store';
import { IAppState } from '@app/store';
import { LikeSuccess } from '@app/+product/store';
import { AlgoliaInsightService } from '../services/algolia-insight.service';
import { ImagesService } from '../../images/services/images.service';

@Component({
  selector: 'vc-catalog-snippet',
  templateUrl: './catalog-snippet.component.html',
  styleUrls: ['./catalog-snippet.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CatalogSnippetComponent implements OnChanges, OnInit {
  @Input()
  product: Product;
  @Input()
  index: number;
  @Input()
  likedItem: string[] = [];
  @Input()
  eventToObserve: Event;
  @Input()
  lazyOff: boolean;
  @Input()
  isArrangement: boolean;
  MinImageLazyLoaded = 3;
  @Input()
  seoContext: string[] = [];
  @Input()
  queryId: string;
  @Input()
  indexName: string;
  @Output()
  eventManager = new EventEmitter<any>();

  isServer = false;
  public expertSellerBadge = false;
  public trustedSellerBadge = false;
  public officialStoreBadge = false;
  public isLocationEnable = false;
  public hostName = '';
  public imgSEOPath = '';
  public isDealActive: boolean;
  public currentDeal: Deal;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    public hostnameLocalizationService: HostnameLocalizationService,
    private store: Store<IAppState>,
    public i18nData: I18nData,
    public algoliaInsightService: AlgoliaInsightService
  ) {
    this.isServer = isPlatformServer(this.platformId);
    this.hostName = this.hostnameLocalizationService.getHostname();
    this.store.pipe(select('catalog')).subscribe(
      res => {
        this.currentDeal = res?.deal || null;
      },
      error => {
        console.error(error);
      }
    );
  }

  ngOnInit() {
    this.imgSEOPath = this.getSEOProductPath(this.product.pictures[0].path);
    this.isDealActive = this.currentDeal?.active && this.product?.flags?.includes('deal-eligible');
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.isServer && changes.product && changes.product.currentValue.isoCountry) {
      this.isLocationEnable = true;
    } else {
      this.isLocationEnable = false;
    }

    if (changes?.product?.currentValue?.flags) {
      this.isDealActive = this.currentDeal?.active && changes.product?.currentValue?.flags.includes('deal-eligible');
    }

    this.getProductBadge();
    this.imgSEOPath = this.getSEOProductPath(this.product.pictures[0].path);
  }

  getProductBadge() {
    this.expertSellerBadge = false; // due to a display issue
    this.trustedSellerBadge = false; // due to a display issue
    const badge = this.product?.seller?.sellerRating?.badge;
    switch (badge) {
      case 'Verkaufsexperte':
      case 'Experte':
      case 'Expert':
      case 'Esperto':
      case 'Experto':
        this.expertSellerBadge = true;
        break;
      case 'Vertrauenswürdiger Verkäufer':
      case 'Trusted':
      case 'Conformidad':
      case 'Recommandé':
      case 'Affidabile':
      case 'De Confianza':
        this.trustedSellerBadge = true;
        break;
    }
  }

  get isLiked(): boolean {
    if (!Array.isArray(this.likedItem)) {
      return false;
    }
    return this.likedItem.includes(String(this.product.id));
  }

  hasSeo(item: string): boolean {
    return this.seoContext.indexOf(item) > 0;
  }

  likeSuccess(value: Boolean) {
    if (value === true) {
      this.store.dispatch(new LikeSuccess(this.product));
    }
  }

  clickProduct(event) {
    this.eventManager.emit({
      positions: [this.index + 1],
      objectIDs: [this.product.id.toString()]
    });
    this.algoliaInsightService.addClickedProduct(this.product.id.toString(), this.indexName, this.queryId);
  }

  get isDirectShipping() {
    return this.product?.flags?.includes('direct-shipping');
  }

  get isOfficialStoreBadge() {
    return this.product?.seller?.isOfficialStore;
  }

  get isLocalCountry(): boolean {
    return this.product?.flags?.includes('local-country') || false;
  }

  getSEOProductPath(imgPath) {
    return ImagesService.improveSEOProductPathImg(imgPath, this.product.path);
  }

  private _setIsDealActive(currentDealActive: boolean) {
    this.isDealActive = currentDealActive && this.product?.flags.includes('deal-eligible');
  }
}
